// src/pages/Reports.js
import React, { useEffect, useMemo, useState } from 'react';
import { useDataContext } from '../contexts/DataContext';
import { StudentDetailsModal } from './Students';
import Tooltip from '../components/Tooltip';

const REPORT_TYPES = [
  { value: 'teacher-verification', label: 'Teacher Verification (Completers + Potential)' },
  { value: 'program', label: 'Program Completers' },
  { value: 'potential', label: 'Potential Completers' },
  { value: 'noncompleters', label: 'Non-Completers with Program Courses' },
  { value: 'cte-indicators', label: 'CTE Indicators Breakdown' },
  { value: 'conversion', label: 'Program Conversion Rate' }
];

const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '');

function Reports() {
  const {
    students,
    programs,
    schoolYears,
    gradYears,
    currentSchoolYear,
    setCurrentSchoolYear,
    currentSemester,
    setCurrentSemester,
    loading
  } = useDataContext();

  const [reportType, setReportType] = useState('teacher-verification');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedGradYear, setSelectedGradYear] = useState('all');
  const [studentDetails, setStudentDetails] = useState(null);

  // Initialize selectedProgram once programs load
  useEffect(() => {
    if (!selectedProgram && programs.length > 0) {
      setSelectedProgram(reportType === 'teacher-verification' ? 'all' : programs[0].id);
    }
  }, [programs, selectedProgram, reportType]);

  // Changing away from teacher-verification while "all" is selected:
  // reset to the first concrete program since other reports are per-program.
  useEffect(() => {
    if (reportType !== 'teacher-verification' && selectedProgram === 'all' && programs.length > 0) {
      setSelectedProgram(programs[0].id);
    }
  }, [reportType, selectedProgram, programs]);

  const filteredStudents = useMemo(() => {
    if (selectedGradYear === 'all') return students;
    return students.filter(s => s.gradYear === selectedGradYear.toString());
  }, [students, selectedGradYear]);

  const reportData = useMemo(() => {
    if (!selectedProgram) return null;

    if (reportType === 'teacher-verification') {
      const targetPrograms = selectedProgram === 'all'
        ? programs
        : programs.filter(p => p.id === selectedProgram);

      const sections = targetPrograms.map(program => {
        const completers = filteredStudents
          .filter(s => s.programStatus[program.id]?.isCompleter)
          .sort(sortByName);
        const potential = filteredStudents
          .filter(s =>
            !s.programStatus[program.id]?.isCompleter &&
            s.programStatus[program.id]?.willBeCompleter
          )
          .sort(sortByName);
        return { program, completers, potential };
      });

      return { type: 'teacher-verification', sections };
    }

    const program = programs.find(p => p.id === selectedProgram);
    if (!program) return null;

    const studentsInProgram = filteredStudents.filter(
      s => (s.programStatus[program.id]?.coursesTaken?.length || 0) > 0
    );

    if (reportType === 'program') {
      const list = filteredStudents.filter(s => s.programStatus[program.id]?.isCompleter);
      return {
        type: 'program',
        title: `CTE Completers - ${program.name}`,
        program,
        students: list,
        summary: {
          totalCompleters: list.length,
          totalStudents: filteredStudents.length,
          totalInProgram: studentsInProgram.length,
          percentage: filteredStudents.length > 0
            ? ((list.length / filteredStudents.length) * 100).toFixed(1)
            : '0.0',
          programPercentage: studentsInProgram.length > 0
            ? ((list.length / studentsInProgram.length) * 100).toFixed(1)
            : '0.0'
        }
      };
    }

    if (reportType === 'potential') {
      const list = filteredStudents.filter(s =>
        !s.programStatus[program.id]?.isCompleter &&
        s.programStatus[program.id]?.willBeCompleter
      );
      return {
        type: 'potential',
        title: `Potential CTE Completers - ${program.name}`,
        program,
        students: list,
        summary: {
          totalPotentialCompleters: list.length,
          totalStudents: filteredStudents.length,
          totalInProgram: studentsInProgram.length,
          programPercentage: studentsInProgram.length > 0
            ? ((list.length / studentsInProgram.length) * 100).toFixed(1)
            : '0.0'
        }
      };
    }

    if (reportType === 'noncompleters') {
      const list = filteredStudents.filter(s => {
        const status = s.programStatus[program.id];
        const has = status && status.coursesTaken && status.coursesTaken.length > 0;
        const notCompleter = !status?.isCompleter && !status?.willBeCompleter;
        return has && notCompleter;
      });
      return {
        type: 'noncompleters',
        title: `Non-Completers with Program Courses - ${program.name}`,
        program,
        students: list,
        summary: {
          totalNonCompleters: list.length,
          totalStudents: filteredStudents.length,
          totalInProgram: studentsInProgram.length,
          programPercentage: studentsInProgram.length > 0
            ? ((list.length / studentsInProgram.length) * 100).toFixed(1)
            : '0.0'
        }
      };
    }

    if (reportType === 'cte-indicators') {
      const bucket = key => filteredStudents.filter(s => s.programStatus[program.id]?.[key]);
      const completers = bucket('isCompleter');
      const concentrators = bucket('isConcentrator');
      const explorers = bucket('isExplorer');
      const participants = bucket('isParticipant');
      const notCTE = bucket('isNotCTE');

      const pct = n => studentsInProgram.length > 0
        ? ((n / studentsInProgram.length) * 100).toFixed(1)
        : '0.0';

      return {
        type: 'cte-indicators',
        title: `CTE Indicators - ${program.name}`,
        program,
        studentsByIndicator: { completers, concentrators, explorers, participants, notCTE },
        summary: {
          totalCompleters: completers.length,
          totalConcentrators: concentrators.length,
          totalExplorers: explorers.length,
          totalParticipants: participants.length,
          totalNotCTE: notCTE.length,
          totalStudents: filteredStudents.length,
          totalInProgram: studentsInProgram.length,
          completerPercentage: pct(completers.length),
          concentratorPercentage: pct(concentrators.length),
          explorerPercentage: pct(explorers.length),
          participantPercentage: pct(participants.length)
        }
      };
    }

    if (reportType === 'conversion') {
      const seniorDrops = filteredStudents.filter(s => {
        const status = s.programStatus[program.id];
        const has = status && status.coursesTaken && status.coursesTaken.length > 0;
        const notCompleter = !status?.isCompleter && !status?.willBeCompleter;
        return has && notCompleter;
      });
      return {
        type: 'conversion',
        title: `Program Conversion - ${program.name}`,
        program,
        students: seniorDrops,
        summary: {
          totalDrops: seniorDrops.length,
          totalSeniorsInProgram: studentsInProgram.length,
          totalInProgram: studentsInProgram.length,
          dropPercentage: studentsInProgram.length > 0
            ? ((seniorDrops.length / studentsInProgram.length) * 100).toFixed(1)
            : '0.0',
          conversionRate: studentsInProgram.length > 0
            ? (100 - ((seniorDrops.length / studentsInProgram.length) * 100)).toFixed(1)
            : '0.0'
        }
      };
    }

    return null;
  }, [reportType, selectedProgram, programs, filteredStudents]);

  const handlePrint = () => window.print();

  const exportToCsv = () => {
    if (!reportData) return;
    let csv = '';

    if (reportData.type === 'teacher-verification') {
      csv += 'Program,Status,Student ID,Student Name,Grad Year,Qualifying Courses\n';
      reportData.sections.forEach(({ program, completers, potential }) => {
        const row = (student, statusLabel) => {
          const status = student.programStatus[program.id];
          const courses = (status?.coursesTaken || [])
            .map(c => {
              const upper = program.upperLevelCourses?.includes(c.courseName) ? ' *' : '';
              const inProg = c.year === currentSchoolYear ? ' (in progress)' : '';
              return `${c.courseName}${upper} [${c.year}, ${c.earnedCredits}cr${inProg}]`;
            })
            .join('; ');
          csv += [
            `"${program.name}"`,
            statusLabel,
            student.id,
            `"${student.name}"`,
            student.gradYear || '',
            `"${courses}"`
          ].join(',') + '\n';
        };
        completers.forEach(s => row(s, 'Current Completer'));
        potential.forEach(s => row(s, 'Potential Completer'));
      });
    } else if (reportData.type === 'cte-indicators') {
      csv += 'CTE Code,Student ID,Student Name,Grad Year,Course Count,Total Credits,Upper Level Credits,Projected Credits,Gaps\n';
      const rows = [
        ['Code 7 (Completer)', reportData.studentsByIndicator.completers],
        ['Code 6 (Concentrator)', reportData.studentsByIndicator.concentrators],
        ['Code E (Explorer)', reportData.studentsByIndicator.explorers],
        ['Code 5 (Participant)', reportData.studentsByIndicator.participants],
        ['Code 4 (Not CTE)', reportData.studentsByIndicator.notCTE]
      ];
      rows.forEach(([label, list]) => {
        list.forEach(student => {
          const status = student.programStatus[selectedProgram];
          csv += [
            label,
            student.id,
            `"${student.name}"`,
            student.gradYear || '',
            status?.courseCount || 0,
            (status?.totalCredits || 0).toFixed(1),
            (status?.upperLevelCredits || 0).toFixed(1),
            (status?.projectedTotalCredits || 0).toFixed(1),
            `"${status?.gaps ? status.gaps.join('; ') : ''}"`
          ].join(',') + '\n';
        });
      });
    } else {
      csv += 'Student ID,Student Name,Grad Year,Program,Status,Course Count,Total Credits,Upper Level Credits,Projected Credits,Gaps\n';
      reportData.students.forEach(student => {
        const status = student.programStatus[selectedProgram];
        let statusText = 'Non-Completer';
        if (status?.isCompleter) statusText = 'Code 7 (Completer)';
        else if (status?.willBeCompleter) statusText = 'Will Complete';
        else if (status?.isConcentrator) statusText = 'Code 6 (Concentrator)';
        else if (status?.isExplorer) statusText = 'Code E (Explorer)';
        else if (status?.isParticipant) statusText = 'Code 5 (Participant)';
        else if (status?.isNotCTE) statusText = 'Code 4 (Not CTE)';

        csv += [
          student.id,
          `"${student.name}"`,
          student.gradYear || '',
          `"${reportData.program.name}"`,
          statusText,
          status?.courseCount || 0,
          (status?.totalCredits || 0).toFixed(1),
          (status?.upperLevelCredits || 0).toFixed(1),
          (status?.projectedTotalCredits || 0).toFixed(1),
          `"${status?.gaps ? status.gaps.join('; ') : ''}"`
        ].join(',') + '\n';
      });
    }

    const filename = reportData.type === 'teacher-verification'
      ? `CTE_Teacher_Verification_${selectedProgram === 'all' ? 'All_Programs' : reportData.sections[0]?.program.name?.replace(/\s+/g, '_')}.csv`
      : `${reportData.title.replace(/\s+/g, '_')}.csv`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <div>Loading report data...</div>;

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 no-print">CTE Completer Reports</h1>

      <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded no-print">
        <h2 className="text-lg font-semibold mb-2">
          Current Academic Period
          <Tooltip text="Set the current school year and semester to correctly calculate potential completers. This helps the system project future credits for in-progress courses." />
        </h2>
        <p className="mb-3 text-sm">
          Set the current academic period to ensure accurate projections for potential completers.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block mb-1 font-semibold">Current School Year:</label>
            <select
              value={currentSchoolYear}
              onChange={(e) => setCurrentSchoolYear(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="">-- No School Year (Summer) --</option>
              {schoolYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-1 font-semibold">Current Semester:</label>
            <select
              value={currentSemester}
              onChange={(e) => setCurrentSemester(e.target.value)}
              className="w-full border rounded px-3 py-2"
              disabled={!currentSchoolYear}
            >
              <option value="1">Semester 1</option>
              <option value="2">Semester 2</option>
            </select>
          </div>
          <div>
            <label className="block mb-1 font-semibold">Graduation Year:</label>
            <select
              value={selectedGradYear}
              onChange={(e) => setSelectedGradYear(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              <option value="all">All Graduation Years</option>
              {gradYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="mb-6 flex flex-col md:flex-row gap-4 no-print">
        <div className="w-full md:w-1/2">
          <label className="block mb-1 font-semibold">Report Type:</label>
          <select
            value={reportType}
            onChange={(e) => setReportType(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            {REPORT_TYPES.map(r => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>
        <div className="w-full md:w-1/2">
          <label className="block mb-1 font-semibold">Program:</label>
          <select
            value={selectedProgram}
            onChange={(e) => setSelectedProgram(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            {reportType === 'teacher-verification' && (
              <option value="all">All Programs (batch print)</option>
            )}
            {programs.map(program => (
              <option key={program.id} value={program.id}>{program.name}</option>
            ))}
          </select>
        </div>
      </div>

      {reportData && reportData.type === 'teacher-verification' && (
        <TeacherVerificationReport
          reportData={reportData}
          currentSchoolYear={currentSchoolYear}
          currentSemester={currentSemester}
          selectedGradYear={selectedGradYear}
          onPrint={handlePrint}
          onExport={exportToCsv}
        />
      )}

      {reportData && reportData.type !== 'teacher-verification' && (
        <StandardReport
          reportType={reportType}
          reportData={reportData}
          selectedProgram={selectedProgram}
          onExport={exportToCsv}
          onPrint={handlePrint}
          onViewDetails={setStudentDetails}
        />
      )}

      {studentDetails && (
        <StudentDetailsModal
          student={studentDetails}
          programs={programs}
          currentSchoolYear={currentSchoolYear}
          onClose={() => setStudentDetails(null)}
        />
      )}
    </div>
  );
}

function TeacherVerificationReport({
  reportData,
  currentSchoolYear,
  currentSemester,
  selectedGradYear,
  onPrint,
  onExport
}) {
  const gradLabel = selectedGradYear === 'all' ? 'All Grad Years' : `Grad Year ${selectedGradYear}`;
  const periodLabel = currentSchoolYear
    ? `${currentSchoolYear} · Semester ${currentSemester}`
    : 'Summer';

  return (
    <div className="teacher-verification-report">
      <div className="flex justify-between items-center mb-4 no-print">
        <h2 className="text-xl font-semibold">
          Teacher Verification Report — {gradLabel}
        </h2>
        <div className="flex gap-2">
          <button
            onClick={onExport}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Export CSV
          </button>
          <button
            onClick={onPrint}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Print
          </button>
        </div>
      </div>

      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm no-print">
        <strong>How to use:</strong> This report combines current completers and
        potential completers for each program. Select "All Programs (batch print)"
        from the program dropdown to produce one packet with page breaks between
        programs — hand each section to the program teacher for verification.
      </div>

      {reportData.sections.map((section, i) => (
        <ProgramVerificationSection
          key={section.program.id}
          section={section}
          currentSchoolYear={currentSchoolYear}
          periodLabel={periodLabel}
          gradLabel={gradLabel}
          isLastSection={i === reportData.sections.length - 1}
        />
      ))}

      {reportData.sections.length === 0 && (
        <div className="p-4 text-center text-gray-500 border rounded">
          No programs to report on.
        </div>
      )}
    </div>
  );
}

function ProgramVerificationSection({
  section,
  currentSchoolYear,
  periodLabel,
  gradLabel,
  isLastSection
}) {
  const { program, completers, potential } = section;

  return (
    <section
      className={`print-section border rounded p-4 bg-white mb-6 ${isLastSection ? '' : 'print-page-break'}`}
    >
      <header className="mb-4 border-b pb-2">
        <h2 className="text-xl font-bold">{program.name}</h2>
        <div className="text-sm text-gray-700">
          CTE Completers Verification · {gradLabel} · {periodLabel}
        </div>
        <div className="text-sm text-gray-700">
          Approval: {program.approvalType === 'statewide' ? 'Statewide' :
                     program.approvalType === 'regional' ? 'Regional' :
                     'Local (Not Approved)'}
          {' · '}
          Current Completers: <strong>{completers.length}</strong>
          {' · '}
          Potential Completers: <strong>{potential.length}</strong>
        </div>
      </header>

      <VerificationTable
        title="Current Completers"
        students={completers}
        program={program}
        currentSchoolYear={currentSchoolYear}
        emptyText="No current completers for this program and filter."
      />

      <VerificationTable
        title="Potential / Expected Completers"
        students={potential}
        program={program}
        currentSchoolYear={currentSchoolYear}
        emptyText="No potential completers for this program and filter."
      />

      <footer className="mt-6 pt-4 border-t text-sm">
        <div className="flex flex-wrap gap-x-8 gap-y-2">
          <div>Verified by: <span className="inline-block border-b border-gray-500 min-w-[250px]">&nbsp;</span></div>
          <div>Date: <span className="inline-block border-b border-gray-500 min-w-[120px]">&nbsp;</span></div>
        </div>
        <div className="text-xs text-gray-500 mt-2">
          ⭐ = Upper-level course. "In progress" = taken during current school year.
        </div>
      </footer>
    </section>
  );
}

function VerificationTable({ title, students, program, currentSchoolYear, emptyText }) {
  return (
    <div className="mb-4">
      <h3 className="text-lg font-semibold mb-2">
        {title} ({students.length})
      </h3>
      {students.length === 0 ? (
        <p className="text-sm text-gray-500">{emptyText}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-3 py-2 text-left w-24">Student ID</th>
                <th className="border px-3 py-2 text-left">Name</th>
                <th className="border px-3 py-2 text-center w-20">Grad Yr</th>
                <th className="border px-3 py-2 text-center w-16">Courses</th>
                <th className="border px-3 py-2 text-center w-16">Credits</th>
                <th className="border px-3 py-2 text-left">Qualifying Courses</th>
                <th className="border px-3 py-2 text-center w-14 no-print">✓</th>
              </tr>
            </thead>
            <tbody>
              {students.map(student => {
                const status = student.programStatus[program.id];
                const credits = status?.isCompleter
                  ? status.totalCredits.toFixed(1)
                  : `${status.totalCredits.toFixed(1)}→${status.projectedTotalCredits.toFixed(1)}`;
                return (
                  <tr key={student.id}>
                    <td className="border px-3 py-2 align-top">{student.id}</td>
                    <td className="border px-3 py-2 align-top">{student.name}</td>
                    <td className="border px-3 py-2 align-top text-center">
                      {student.gradYear || '—'}
                    </td>
                    <td className="border px-3 py-2 align-top text-center">
                      {status.courseCount}
                    </td>
                    <td className="border px-3 py-2 align-top text-center">
                      {credits}
                    </td>
                    <td className="border px-3 py-2 align-top">
                      <ul className="space-y-0.5">
                        {status.coursesTaken.map((c, i) => {
                          const isUpper = program.upperLevelCourses?.includes(c.courseName);
                          const inProgress = c.year === currentSchoolYear;
                          return (
                            <li key={i}>
                              {isUpper && <span title="Upper level">⭐ </span>}
                              <span className={isUpper ? 'font-semibold' : ''}>
                                {c.courseName}
                              </span>
                              <span className="text-gray-600">
                                {' '}({c.year}, {c.earnedCredits}cr
                                {inProgress ? ', in progress' : ''})
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </td>
                    <td className="border px-3 py-2 align-top text-center no-print">&nbsp;</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StandardReport({ reportType, reportData, selectedProgram, onExport, onPrint, onViewDetails }) {
  return (
    <div className="border rounded p-4 bg-white">
      <div className="flex justify-between items-center mb-4 no-print">
        <h2 className="text-xl font-semibold">{reportData.title}</h2>
        <div className="flex gap-2">
          <button
            onClick={onExport}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Export CSV
          </button>
          <button
            onClick={onPrint}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Print
          </button>
        </div>
      </div>

      <div className="mb-4 bg-gray-50 p-3 rounded">
        <h3 className="font-semibold mb-2">Summary</h3>
        <StandardReportSummary reportType={reportType} reportData={reportData} />
      </div>

      {reportType === 'cte-indicators' ? (
        <CTEIndicatorsView
          reportData={reportData}
          selectedProgram={selectedProgram}
          onViewDetails={onViewDetails}
        />
      ) : reportData.students.length === 0 ? (
        <div className="p-4 text-center text-gray-500">No students found matching these criteria</div>
      ) : (
        <StudentTable
          students={reportData.students}
          reportType={reportType}
          selectedProgram={selectedProgram}
          onViewDetails={onViewDetails}
        />
      )}
    </div>
  );
}

function StandardReportSummary({ reportType, reportData }) {
  const s = reportData.summary;
  if (reportType === 'program') {
    return (
      <div className="space-y-1">
        <p>Total Completers: {s.totalCompleters} students</p>
        <p>
          Completion Rate (All Students): {s.percentage}% of all {s.totalStudents} students
          <Tooltip text="Percentage of all students who have completed this program" />
        </p>
        <p>
          Completion Rate (Program Students): {s.programPercentage}% of {s.totalInProgram} students in this program
          <Tooltip text="Percentage of students in the program who have completed it" />
        </p>
      </div>
    );
  }
  if (reportType === 'potential') {
    return (
      <div className="space-y-1">
        <p>
          Potential Completers: {s.totalPotentialCompleters} students will complete by end of current period
          <Tooltip text="Students who will meet all completer requirements once current in-progress courses are completed" />
        </p>
        <p>Program share: {s.programPercentage}% of {s.totalInProgram} students in this program</p>
      </div>
    );
  }
  if (reportType === 'noncompleters') {
    return (
      <div className="space-y-1">
        <p>Non-Completers: {s.totalNonCompleters} students</p>
        <p>Program share: {s.programPercentage}% of {s.totalInProgram} students in this program</p>
      </div>
    );
  }
  if (reportType === 'cte-indicators') {
    return (
      <div className="space-y-1">
        <p><span className="font-semibold text-green-600">Code 7 (Completers):</span> {s.totalCompleters} ({s.completerPercentage}%)</p>
        <p><span className="font-semibold text-blue-600">Code 6 (Concentrators):</span> {s.totalConcentrators} ({s.concentratorPercentage}%)</p>
        <p><span className="font-semibold text-purple-600">Code E (Explorers):</span> {s.totalExplorers} ({s.explorerPercentage}%)</p>
        <p><span className="font-semibold text-orange-600">Code 5 (Participants):</span> {s.totalParticipants} ({s.participantPercentage}%)</p>
        <p className="mt-2">Total in Program: {s.totalInProgram} of {s.totalStudents}</p>
      </div>
    );
  }
  if (reportType === 'conversion') {
    return (
      <div className="space-y-1">
        <p>Program Conversion Rate: <span className="font-semibold">{s.conversionRate}%</span></p>
        <p>Drop Rate: {s.dropPercentage}% of {s.totalSeniorsInProgram} in program</p>
        <p>Total Drops: {s.totalDrops}</p>
      </div>
    );
  }
  return null;
}

function StudentTable({ students, reportType, selectedProgram, onViewDetails }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="border px-4 py-2 text-left">Student ID</th>
            <th className="border px-4 py-2 text-left">Name</th>
            <th className="border px-4 py-2 text-center">Grad Year</th>
            <th className="border px-4 py-2 text-center">Courses</th>
            <th className="border px-4 py-2 text-center">Credits</th>
            <th className="border px-4 py-2 text-center">Upper Level</th>
            {reportType !== 'program' && <th className="border px-4 py-2 text-left">Gaps</th>}
            <th className="border px-4 py-2 text-center no-print">Actions</th>
          </tr>
        </thead>
        <tbody>
          {students.map(student => {
            const status = student.programStatus[selectedProgram];
            return (
              <tr key={student.id} className="hover:bg-gray-50">
                <td className="border px-4 py-2">{student.id}</td>
                <td className="border px-4 py-2">{student.name}</td>
                <td className="border px-4 py-2 text-center">{student.gradYear || 'Unknown'}</td>
                <td className="border px-4 py-2 text-center">{status?.courseCount}/3</td>
                <td className="border px-4 py-2 text-center">
                  {status?.totalCredits.toFixed(1)}/4.0
                  {reportType === 'potential' && status?.projectedTotalCredits > status?.totalCredits && (
                    <span className="text-blue-600 ml-1">→ {status?.projectedTotalCredits.toFixed(1)}</span>
                  )}
                </td>
                <td className="border px-4 py-2 text-center">
                  {status?.hasUpperLevelCourse ? 'Yes' : 'No'}
                  {reportType === 'potential' && !status?.hasUpperLevelCourse && status?.willHaveUpperLevelCourse && (
                    <span className="text-blue-600 ml-1">→ Yes (Projected)</span>
                  )}
                </td>
                {reportType !== 'program' && (
                  <td className="border px-4 py-2">
                    {status?.gaps?.length > 0 ? (
                      <ul className="list-disc list-inside">
                        {status.gaps.map((gap, i) => <li key={i}>{gap}</li>)}
                      </ul>
                    ) : (
                      <span className="text-gray-500">None</span>
                    )}
                  </td>
                )}
                <td className="border px-4 py-2 text-center no-print">
                  <button
                    onClick={() => onViewDetails(student)}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CTEIndicatorsView({ reportData, selectedProgram, onViewDetails }) {
  const sections = [
    { key: 'completers', label: 'Code 7 (Completers)', color: 'text-green-600' },
    { key: 'concentrators', label: 'Code 6 (Concentrators)', color: 'text-blue-600' },
    { key: 'explorers', label: 'Code E (Explorers)', color: 'text-purple-600' },
    { key: 'participants', label: 'Code 5 (Participants)', color: 'text-orange-600' },
    { key: 'notCTE', label: 'Code 4 (Not CTE)', color: 'text-gray-600' }
  ];

  return (
    <div>
      {sections.map(({ key, label, color }) => {
        const list = reportData.studentsByIndicator[key];
        const isNotCTE = key === 'notCTE';
        return (
          <div key={key} className="mb-6">
            <h3 className={`text-lg font-semibold ${color} mb-2`}>{label}</h3>
            {list.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No students with this indicator</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border px-4 py-2 text-left">Student ID</th>
                      <th className="border px-4 py-2 text-left">Name</th>
                      <th className="border px-4 py-2 text-center">Grad Year</th>
                      {!isNotCTE && (
                        <>
                          <th className="border px-4 py-2 text-center">Courses</th>
                          <th className="border px-4 py-2 text-center">Credits</th>
                          <th className="border px-4 py-2 text-center">Upper Level</th>
                          <th className="border px-4 py-2 text-left">Gaps</th>
                        </>
                      )}
                      <th className="border px-4 py-2 text-center no-print">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.map(student => {
                      const status = student.programStatus[selectedProgram];
                      return (
                        <tr key={student.id} className="hover:bg-gray-50">
                          <td className="border px-4 py-2">{student.id}</td>
                          <td className="border px-4 py-2">{student.name}</td>
                          <td className="border px-4 py-2 text-center">{student.gradYear || 'Unknown'}</td>
                          {!isNotCTE && (
                            <>
                              <td className="border px-4 py-2 text-center">{status?.courseCount}/3</td>
                              <td className="border px-4 py-2 text-center">{status?.totalCredits.toFixed(1)}/4.0</td>
                              <td className="border px-4 py-2 text-center">{status?.hasUpperLevelCourse ? 'Yes' : 'No'}</td>
                              <td className="border px-4 py-2">
                                {status?.gaps?.length > 0 ? (
                                  <ul className="list-disc list-inside">
                                    {status.gaps.map((gap, i) => <li key={i}>{gap}</li>)}
                                  </ul>
                                ) : (
                                  <span className="text-gray-500">None</span>
                                )}
                              </td>
                            </>
                          )}
                          <td className="border px-4 py-2 text-center no-print">
                            <button
                              onClick={() => onViewDetails(student)}
                              className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default Reports;
